// ============================================
// LIGA AJUPAM - Sesión del staff de playoffs (v6.11.208)
// ============================================
// Juez de cancha, recepción y seguridad comparten el mismo patrón de sesión: un token
// en localStorage que se re-valida contra el servidor. Este archivo concentra
// las dos decisiones que en los playoffs de la 4ta edición salieron mal:
//
// 1. CUÁNDO UN ERROR SIGNIFICA "TU SESIÓN YA NO SIRVE"
//    Los proveedores borraban el token ante CUALQUIER error de la validación:
//    un timeout con el WiFi de la cancha, un corte al volver de la cámara, un
//    502 del gateway… y el juez de cancha tenía que volver a tipear usuario y PIN en
//    medio de un partido. En producción quedaron sesiones de 0 s, 3 y 7 min.
//    Ahora sólo cuenta como sesión inválida lo que el servidor dice que lo es:
//    los validadores (`_validate_opener_session` y hermanos) lanzan siempre con
//    SQLSTATE 28000 (invalid_authorization_specification).
//
// 2. CADA CUÁNTO RE-VALIDAR AL VOLVER A LA APP
//    Se re-validaba en CADA `visibilitychange` (bloquear la pantalla, pasar a
//    WhatsApp, abrir la cámara nativa) poniendo el proveedor en "cargando", lo
//    que desmontaba toda la herramienta. Cada RPC ya valida el token del lado
//    del servidor, así que la re-validación al volver es sólo para enterarse
//    antes de que venció: alcanza con hacerla en silencio y de vez en cuando.
//
// Puro y sin imports: lo prueba `npm run test:staff-session`.

/** Mínimo entre re-validaciones silenciosas al volver a primer plano. */
export const STAFF_REVALIDATE_MIN_MS = 5 * 60_000;

/** SQLSTATE con el que los validadores de sesión del staff rechazan un token. */
export const STAFF_AUTH_SQLSTATE = '28000';

/**
 * ¿Este error dice que el token ya no sirve (y hay que volver al login)?
 *
 * Sólo el SQLSTATE 28000 lo dice. Un timeout, un error de red o cualquier otro
 * fallo NO: la sesión puede seguir viva y el próximo intento va a andar.
 */
export function isStaffAuthError(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false;
  const code = (e as { code?: unknown }).code;
  return code === STAFF_AUTH_SQLSTATE;
}

/**
 * ¿Toca re-validar al volver a primer plano?
 *
 * @param lastValidatedAt  epoch ms de la última validación exitosa (null = nunca)
 * @param now              epoch ms actual
 * @param minMs            separación mínima entre validaciones
 */
export function shouldRevalidateStaffSession(
  lastValidatedAt: number | null,
  now: number,
  minMs: number = STAFF_REVALIDATE_MIN_MS,
): boolean {
  if (lastValidatedAt === null || !Number.isFinite(lastValidatedAt)) return true;
  // Reloj que retrocede (cambio de hora del teléfono): re-validar, no esperar.
  if (now < lastValidatedAt) return true;
  return now - lastValidatedAt >= minMs;
}

/** Error con el SQLSTATE de sesión inválida, para respuestas vacías del validador. */
export function staffAuthError(message = 'Sesión inválida'): Error {
  const err = new Error(message) as Error & { code?: string };
  err.code = STAFF_AUTH_SQLSTATE;
  return err;
}
